/**
 * Легенда культур карты — ОДИН источник на всех, кто про неё говорит.
 *
 * Вынесено из `CulturePalette` (там хук жил приватно), потому что появился
 * второй потребитель — попап поля. UAT founder 10.08: попап показывает «Лён», а
 * легенда красит то же поле «Не разобрано», и человек не понимает, почему поле
 * серое. Разъезд возник ровно потому, что попап брал имя из тайла, а вердикт
 * «культура признана справочником хозяйства» считает сервер в `/scope/crops`
 * (см. `services/crop_legend.in_catalog_predicate`).
 *
 * Пока оба экрана читают ОДИН этот ответ, разойтись они не могут: попап
 * помечает культуру непризнанной ровно тогда, когда легенда унесла поле в
 * «Не разобрано».
 *
 * `queryKey` намеренно совпадает с прежним ключом палитры — легенда и попап
 * делят кэш, второго запроса не появляется.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ScopeCropEntry {
  /** `null` — это строка-агрегат «Не разобрано», а не культура. */
  crop_id: string | null;
  name: string;
  color: string;
  field_count: number;
}

interface ScopeCropsResponse {
  scope: { type: string; id: string | null };
  crops: ScopeCropEntry[];
}

export function useScopeCrops(scope: {
  type: string;
  id: string | null;
}): UseQueryResult<ScopeCropEntry[], Error> {
  return useQuery<ScopeCropEntry[], Error>({
    queryKey: ['scope-crops', scope.type, scope.id],
    queryFn: async () => {
      const params = new URLSearchParams({ scope_type: scope.type });
      if (scope.id) params.set('scope_id', scope.id);
      const resp = await api.get<ScopeCropsResponse>(
        `/api/v1/scope/crops?${params.toString()}`,
      );
      return resp.crops ?? [];
    },
    // Культуры меняются редко (новый посев / смена статуса), но баг founder'а
    // показал, что протухание видно глазом. 60с — баланс.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Признана ли культура справочником хозяйства — ГЛАЗАМИ ЛЕГЕНДЫ.
 *
 * `true` — культура в легенде есть (поле покрашено её цветом);
 * `false` — легенда загружена, культура в ней не значится, то есть поле
 * попало в «Не разобрано»;
 * `null` — ответить нечем (легенда не загрузилась, культура не указана).
 * `null` — это не «нет», а «не знаю»: подписывать культуру непризнанной
 * наугад значит врать пользователю в обратную сторону.
 */
export function isCropInLegend(
  crops: ScopeCropEntry[] | undefined,
  cropId: string | null | undefined,
): boolean | null {
  if (!cropId || !crops || crops.length === 0) return null;
  return crops.some((c) => c.crop_id === cropId);
}
